import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getNotifications,
  readNotification,
  readAllNotifications,
} from "../../actions/notifications.actions";
import ImageProfil from "../Profil/ImageProfil";
import {
  Bell,
  GraduationCap,
  BookOpen,
  Bot,
  X,
  ThumbsUp,
  MessageCircle,
  MessageSquare,
  AtSign,
  Newspaper,
  Wrench,
  RotateCcw,
  Trophy,
  Flame,
  Clock,
  CheckCircle2,
  CheckCheck,
  Reply,
} from "lucide-react";
import Push from "push.js";
import logoPush from "../../asset/logo-push.jpg";
import { Link } from "react-router-dom";
import parse from "html-react-parser";

// Notifs liées à la Skills Academy : ligne mise en valeur dans la couleur de
// leur accent (barre + fond teinté).
const FORMATION_NOTIF_TYPES = [
  "new_formation",
  "formation_to_retake",
  "formation_completed",
  "formation_reminder",
];
// accent → [couleur de la barre, fond translucide]
const NOTIF_ACCENT_COLORS = {
  green: ["#22b35a", "rgba(34, 179, 90, 0.13)"],
  gold: ["#f5a302", "rgba(245, 163, 2, 0.14)"],
  orange: ["#f0792e", "rgba(240, 121, 46, 0.14)"],
  purple: ["#8b5cf6", "rgba(139, 92, 246, 0.14)"],
};

const Notification = ({ user, socket }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [readNotifications, setReadNotifications] = useState([]);
  // IDs déjà traités pour le push : évite de re-pousser une notif connue.
  const pushedIdsRef = useRef(new Set());
  // Au tout premier chargement on enregistre l'existant comme "déjà vu" sans
  // pousser : sinon 50 notifs accumulées = 50 push d'un coup.
  const firstLoadRef = useRef(true);
  const dispatch = useDispatch();

  const notificationData = useSelector((state) => state.notificationReducer);
  const myNotification = Object.values(notificationData).filter(
    (notification) => {
      return notification;
    }
  );

  const unreadNotifications = myNotification?.filter(
    (notification) =>
      !notification.read && !readNotifications?.includes(notification.id)
  );

  useEffect(() => {
    socket?.on("getNotification", (notification) => {
      dispatch(getNotifications(notification.receiverId));
    });

    socket?.on("getNotificationPost", () => {
      const userId = user;
      dispatch(getNotifications(userId));
    });

    socket?.on("getChatbotQuestionAnswered", () => {
      // On se contente de rafraîchir : le push est géré (et dédupliqué) par
      // l'effet ci-dessous via la notif "chatbot_question_answered". Évite le
      // double push (socket + effet) pour le même évènement.
      dispatch(getNotifications(user));
    });
  }, [user]);

  useEffect(() => {
    dispatch(getNotifications(user));
  }, [dispatch]);

  function extractHashtags(message) {
    const regex = /#(\w+)/g;
    const hashtags = [];
    let match;

    while ((match = regex.exec(message)) !== null) {
      hashtags.push(match[0]);
    }

    return hashtags;
  }

  // Demande la permission de notification. DOIT être appelé depuis un geste
  // utilisateur (clic) : les navigateurs bloquent une demande faite au chargement.
  const ensurePushPermission = () => {
    try {
      if (
        Push.Permission?.get &&
        Push.Permission.get() === Push.Permission.DEFAULT
      ) {
        Push.Permission.request();
      }
    } catch (e) {
      // permission non supportée : on ignore
    }
  };

  //Menu toggle
  const toggleMenu = () => {
    ensurePushPermission();
    setIsOpen(!isOpen);
  };

  const markAsRead = (notificationId) => {
    setReadNotifications([...readNotifications, notificationId]);
    dispatch(readNotification(notificationId));
  };

  // "Tout lire" : supprime toutes les notifs de l'utilisateur en une requête.
  const markAllAsRead = () => {
    const ids = unreadNotifications?.map((n) => n.id) || [];
    if (ids.length === 0) return;
    setReadNotifications([...readNotifications, ...ids]);
    dispatch(readAllNotifications(user));
  };

  // Construit le titre + corps d'un push pour une notif (texte brut, pas de HTML).
  const buildPushContent = (notification) => {
    const who = notification?.user?.firstname;
    const plain = (notification?.comment || "").replace(/<[^>]*>/g, "");
    switch (notification?.type) {
      case "like":
        return { title: "Nouvelle notification", body: `${who} a trouvé(e) votre post utile` };
      case "comment":
        return { title: "Nouvelle notification", body: `${who} a commenté(e) votre post : "${plain}"` };
      case "commentTuto":
        return { title: "Nouvelle notification", body: `${who} a commenté(e) votre tuto : "${plain}"` };
      case "mention_tuto":
        return { title: "Nouvelle notification", body: `${who} vous a mentionné dans un tutoriel` };
      case "mention_post":
        return { title: "Nouvelle notification", body: `${who} vous a mentionné dans un post` };
      case "new_post":
        return { title: "Nouvelle notification", body: `${who} a créé un nouveau post !` };
      case "new_tools":
        return { title: "Nouvelle notification", body: `${who} a ajouté un nouvel outil` };
      case "new_formation":
        return { title: "Nouvelle formation", body: `Une nouvelle formation est disponible : « ${notification?.comment} »` };
      case "formation_to_retake":
        return { title: "Formation à repasser", body: `« ${notification?.comment} » a été mise à jour` };
      case "formation_completed":
        return { title: "Badge décroché !", body: `Bravo ! Vous avez validé « ${notification?.comment} »` };
      case "new_tuto":
        return { title: "Nouveau tutoriel", body: `Un nouveau tutoriel est disponible : « ${notification?.comment} »` };
      case "rank_overtaken":
        return { title: "Vous avez été dépassé !", body: `${who || "Quelqu'un"} vous a doublé au classement` };
      case "formation_reminder":
        return { title: "Reprenez votre formation", body: `« ${notification?.comment} » vous attend` };
      case "knowledge_done":
        return { title: "Demande traitée", body: `Votre demande a été traitée` };
      case "comment_reply":
        return { title: "Nouvelle réponse", body: `${who || "Quelqu'un"} a répondu à votre commentaire` };
      case "chatbot_question_answered": {
        const q = notification?.chatbotQuestion || "";
        return {
          title: "Nouvelle réponse chatbot",
          body: `${who || "Un administrateur"} a répondu à une question en attente pour Skilly : "${q.substring(0, 50)}${q.length > 50 ? "..." : ""}"`,
        };
      }
      default:
        return null;
    }
  };

  const firePush = (title, body) => {
    Push.create(title, {
      body,
      icon: logoPush,
      timeout: 120000,
      onClick: function () {
        window.focus();
        this.close();
      },
    });
  };

  //push notification
  useEffect(() => {
    if (!unreadNotifications) return;

    // Premier rendu : on enregistre l'existant comme "déjà vu" sans rien pousser.
    // Sans ça, N notifs déjà accumulées déclenchaient N push d'un coup au chargement.
    if (firstLoadRef.current) {
      unreadNotifications.forEach((n) => pushedIdsRef.current.add(n.id));
      firstLoadRef.current = false;
      return;
    }

    // Notifs réellement nouvelles, identifiées par id (et non par un index de slice).
    const fresh = unreadNotifications.filter(
      (n) => !pushedIdsRef.current.has(n.id)
    );
    if (fresh.length === 0) return;
    fresh.forEach((n) => pushedIdsRef.current.add(n.id));

    // Pas de push si l'utilisateur n'a pas accordé la permission navigateur
    // (sinon push.js lève "permission request declined" → erreur non gérée).
    let canPush = false;
    try {
      canPush = typeof Push.Permission?.has === "function" && Push.Permission.has();
    } catch (e) {
      canPush = false;
    }
    if (!canPush) return;

    try {
      // Plusieurs nouvelles d'un coup → un seul push agrégé (jamais une rafale).
      if (fresh.length > 3) {
        firePush(
          "Nouvelles notifications",
          `Vous avez ${fresh.length} nouvelles notifications`
        );
        return;
      }
      fresh.forEach((notification) => {
        const content = buildPushContent(notification);
        if (content) firePush(content.title, content.body);
      });
    } catch (e) {
      // permission refusée / push non supporté : on ignore silencieusement
    }
  }, [unreadNotifications]);

  // Mappe un type de notification vers son rendu. Chaque type a son icône + sa
  // couleur (accent) distincte. `userId` → avatar de l'expéditeur (+ pastille type).
  const getNotifMeta = (n) => {
    const who = n.user?.firstname || "Quelqu'un";
    switch (n.type) {
      case "like":
        return { to: `/post/${n.postId}`, userId: n.user?.id, Icon: ThumbsUp, accent: "rose", title: who, sub: "a trouvé votre post utile" };
      case "comment":
        return {
          to: `/post/${n.postId}`,
          userId: n.user?.id,
          Icon: MessageCircle,
          accent: "blue",
          title: who,
          sub: <>a commenté votre post : « {parse(n.comment || "")} »</>,
        };
      case "commentTuto":
        return {
          to: `/tutoriels/${n.postId}`,
          userId: n.user?.id,
          Icon: MessageSquare,
          accent: "sky",
          title: who,
          sub: <>a commenté votre tuto : « {parse(n.comment || "")} »</>,
        };
      case "mention_tuto":
        return { to: `/tutoriels/${n.postId}`, userId: n.user?.id, Icon: AtSign, accent: "violet", title: who, sub: "vous a mentionné dans un tutoriel" };
      case "mention_post":
        return { to: `/post/${n.postId}`, userId: n.user?.id, Icon: AtSign, accent: "violet", title: who, sub: "vous a mentionné dans un post" };
      case "new_post":
        return {
          to: `/post/${n.postId}`,
          userId: n.user?.id,
          Icon: Newspaper,
          accent: "slate",
          title: who,
          sub: <>a créé un nouveau post : <b>{extractHashtags(n.hashtag || "")[0]}</b></>,
        };
      case "new_tools":
        return { to: `/convertisseur`, userId: n.user?.id, Icon: Wrench, accent: "amber", title: who, sub: "a ajouté un nouvel outil" };
      case "new_formation":
        return {
          to: `/formations/${n.postId}`,
          Icon: BookOpen,
          accent: "green",
          title: "Nouvelle formation",
          sub: <>« {n.comment} » est disponible</>,
        };
      case "formation_to_retake":
        return {
          to: `/formations`,
          Icon: RotateCcw,
          accent: "orange",
          title: "Formation à repasser",
          sub: <>« {n.comment} » a été mise à jour</>,
        };
      case "formation_completed":
        return {
          to: `/formations/${n.postId}`,
          Icon: Trophy,
          accent: "gold",
          title: "Badge décroché !",
          sub: <>Vous avez validé « {n.comment} »</>,
        };
      case "new_tuto":
        return {
          to: `/tutoriels/${n.postId}`,
          Icon: GraduationCap,
          accent: "teal",
          title: "Nouveau tutoriel",
          sub: <>« {n.comment} » est disponible</>,
        };
      case "rank_overtaken":
        return {
          to: `/`,
          userId: n.user?.id,
          Icon: Flame,
          accent: "red",
          title: "Vous avez été dépassé !",
          sub: <>{who} vous a doublé au classement</>,
        };
      case "formation_reminder":
        return {
          to: `/formations/${n.postId}`,
          Icon: Clock,
          accent: "purple",
          title: "Reprenez votre formation",
          sub: <>« {n.comment} » vous attend</>,
        };
      // "knowledge" = ancien type (avant renommage) ; on le traite comme "knowledge_done".
      case "knowledge":
      case "knowledge_done":
        return {
          to: `/tutoriels`,
          Icon: CheckCircle2,
          accent: "emerald",
          title: "Demande traitée",
          sub: <>Votre demande{n.comment ? ` « ${n.comment} »` : ""} a été traitée</>,
        };
      case "comment_reply":
        return {
          to: `/post/${n.postId}`,
          userId: n.user?.id,
          Icon: Reply,
          accent: "cyan",
          title: who,
          sub: <>a répondu à votre commentaire : « {parse(n.comment || "")} »</>,
        };
      case "chatbot_question_answered":
        return {
          to: null,
          userId: n.user?.id,
          Icon: Bot,
          accent: "indigo",
          title: who,
          sub: "a répondu à une question Skilly",
          chatbot: true,
        };
      // Repli générique : un type inconnu/hérité reste visible (jamais compté sans
      // être affiché) au lieu d'être silencieusement ignoré.
      default:
        return {
          to: n.postId ? `/post/${n.postId}` : null,
          userId: n.user?.id,
          Icon: Bell,
          accent: "slate",
          title: n.user?.firstname || "Notification",
          sub: n.comment
            ? `« ${n.comment} »`
            : "Vous avez une nouvelle notification",
        };
    }
  };

  const renderNotif = (n) => {
    const meta = getNotifMeta(n);
    if (!meta) return null;
    const Icon = meta.Icon;
    const badge = meta.userId ? (
      <span className="notif-badge notif-badge-avatar">
        <ImageProfil id={meta.userId} />
        {Icon && (
          <span className={`notif-type-dot accent-${meta.accent}`}>
            <Icon size={11} />
          </span>
        )}
      </span>
    ) : (
      <span className={`notif-badge accent-${meta.accent}`}>{Icon && <Icon size={18} />}</span>
    );
    const inner = (
      <>
        {badge}
        <div className="notif-body">
          <p className="notif-title">{meta.title}</p>
          <span className="notif-sub">{meta.sub}</span>
          {meta.chatbot && (
            <div className="notif-qa">
              <div className="notif-qa-row">
                <span className="notif-qa-label">Question</span>
                <p>« {n.chatbotQuestion} »</p>
              </div>
              <div className="notif-qa-row">
                <span className="notif-qa-label answer">Réponse</span>
                <p>{n.chatbotReponse}</p>
              </div>
            </div>
          )}
        </div>
      </>
    );
    const isMention = n.type === "mention_post" || n.type === "mention_tuto";
    const isFormation = FORMATION_NOTIF_TYPES.includes(n.type);
    const accentColors = isFormation ? NOTIF_ACCENT_COLORS[meta.accent] : null;
    const accentStyle = accentColors
      ? { "--nf-bar": accentColors[0], "--nf-bg": accentColors[1] }
      : undefined;
    return (
      <div
        className={`notif-item${isMention ? " is-mention" : ""}${
          isFormation ? " is-formation" : ""
        }`}
        style={accentStyle}
        key={n.id}
      >
        {meta.to ? (
          <Link className="notif-link" to={meta.to} onClick={() => markAsRead(n.id)}>
            {inner}
          </Link>
        ) : (
          <div className="notif-link" onClick={() => markAsRead(n.id)}>
            {inner}
          </div>
        )}
        <button
          className="notif-close"
          title="Marquer comme lu"
          onClick={(e) => {
            e.stopPropagation();
            markAsRead(n.id);
          }}
        >
          <X size={15} />
        </button>
      </div>
    );
  };

  return (
    <>
      <div className="notificaion" onClick={toggleMenu}>
        <Bell
          size={17}
          className={unreadNotifications?.length > 0 ? "bell-shake" : ""}
        />
        {unreadNotifications?.length > 0 && (
          <span className="badge">{unreadNotifications?.length}</span>
        )}
      </div>
      <div className={`profil_notif_edit ${isOpen ? "active_edit_notif" : ""}`}>
        <div className="notif-head">
          <h3>Mes notifications</h3>
          {unreadNotifications?.length > 0 && (
            <span className="notif-count">{unreadNotifications.length}</span>
          )}
          {unreadNotifications?.length > 0 && (
            <button
              className="notif-read-all"
              onClick={markAllAsRead}
              title="Tout marquer comme lu"
            >
              <CheckCheck size={15} />
              <span>Tout lire</span>
            </button>
          )}
          <button className="notif-head-close" onClick={toggleMenu} title="Fermer">
            <X size={18} />
          </button>
        </div>
        <div className="notif-list">
          {unreadNotifications?.length > 0 ? (
            unreadNotifications.map((notification) => renderNotif(notification))
          ) : (
            <div className="notif-empty">
              <Bell size={26} />
              <p>Aucune notification</p>
              <span>Vous êtes à jour.</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Notification;
